// Help index — public catalog browser. Owner/operator audiences land
// here to look up a pro article when a tenant asks them a question
// in person (per Peter's 2026-05-26 decision); pros/leads land here
// when Rosie hands off without a direct slug.
//
// Articles are grouped by surface using SURFACE_LABELS, ordered as
// returned by the RPC (surface, priority, title) which already puts
// p0 above p1 above p2 inside each surface.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHelpArticleList, surfaceLabel, type HelpArticleListItem } from '@/lib/help';
import { PRODUCT_NAME } from '@/lib/brand';

const PRIORITY_LABEL: Record<string, string> = {
  p0: 'Most asked',
  p1: 'Common',
  p2: 'Long-tail',
};

export default function Help() {
  const { data, isLoading, isError, error } = useHelpArticleList();
  const [filter, setFilter] = useState('');

  const grouped = useMemo(() => {
    const rows = (data ?? []) as HelpArticleListItem[];
    const q = filter.trim().toLowerCase();
    const matches = q
      ? rows.filter(
          (r) =>
            r.title.toLowerCase().includes(q) ||
            r.slug.toLowerCase().includes(q),
        )
      : rows;
    const bySurface = new Map<string, HelpArticleListItem[]>();
    for (const r of matches) {
      const arr = bySurface.get(r.surface) ?? [];
      arr.push(r);
      bySurface.set(r.surface, arr);
    }
    return Array.from(bySurface.entries());
  }, [data, filter]);

  return (
    <div className="min-h-screen bg-brand-fog">
      <header className="px-6 pt-12 pb-6 text-center border-b border-brand-mist">
        <a href="/" className="inline-block">
          <h1 className="font-display text-3xl font-extrabold text-image-black">IMAGE <span className="text-image-pink">OS</span></h1>
        </a>
        <p className="text-sm text-brand-slate mt-1">Help</p>
      </header>

      <main className="px-6 py-10 max-w-3xl mx-auto">
        <p className="text-sm text-brand-slate mb-6">
          Browse step-by-step articles for the Pro Hub and the rest of {PRODUCT_NAME}.
          A pro just asked you a question? Search the title and forward the link.
        </p>

        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search articles…"
          className="w-full rounded-lg border border-image-border bg-white px-4 py-3 text-sm text-image-black placeholder-image-charcoal/60 focus:outline-none focus:ring-2 focus:ring-image-pink/40 focus:border-image-pink"
        />

        {isLoading && <p className="mt-8 text-sm text-brand-slate">Loading articles…</p>}

        {isError && (
          <p className="mt-8 text-sm text-brand-slate">
            We couldn’t load the article list.{' '}
            <span className="text-xs text-brand-slate/70">{(error as Error)?.message}</span>
          </p>
        )}

        {!isLoading && !isError && grouped.length === 0 && (
          <p className="mt-8 text-sm text-brand-slate">
            {filter ? 'No articles match that search.' : 'No articles have been published yet.'}
          </p>
        )}

        <div className="mt-8 space-y-10">
          {grouped.map(([surface, rows]) => (
            <section key={surface}>
              <h2 className="font-display text-lg text-brand-deep">{surfaceLabel(surface)}</h2>
              <ul className="mt-3 divide-y divide-brand-mist">
                {rows.map((row) => (
                  <li key={row.slug} className="py-3">
                    <Link
                      to={`/help/${row.slug}`}
                      className="flex items-baseline justify-between gap-3 group"
                    >
                      <span className="text-sm text-brand-deep group-hover:text-image-pink transition-colors">
                        {row.title}
                      </span>
                      {row.priority && (
                        <span
                          className="shrink-0 text-[10px] uppercase tracking-[0.16em] text-brand-slate/70"
                          title={`Priority ${row.priority}`}
                        >
                          {PRIORITY_LABEL[row.priority] ?? row.priority}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>

      <footer className="px-6 py-8 max-w-3xl mx-auto text-center border-t border-brand-mist">
        <a href="/" className="text-sm text-brand-slate hover:text-image-pink">
          &larr; Back to the Pro Hub
        </a>
      </footer>
    </div>
  );
}
